import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { User, LoginCredentials } from '@/types/auth';
import { authService } from '@/services/authService';

// Readonly interface enforces that AuthState is only mutated via Immer drafts
// inside createSlice reducers — never via direct external assignment.
interface AuthState {
  readonly user: User | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly isInitialized: boolean; // True after first auth check attempt
  readonly error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false, // Will be confirmed by /auth/me on app startup
  isLoading: false,
  isInitialized: false, // Becomes true after fetchCurrentUser completes (success or fail)
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      // Cookie-based: backend sets httpOnly cookie; response body has user info
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Login failed');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Failed to fetch user';

      // Pass both status and message for smart error handling
      return rejectWithValue({
        status,
        message: status === 401 ? 'Unauthorized' : detail
      });
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      await authService.changePassword(oldPassword, newPassword);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to change password');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.isInitialized = true;

        // login thunk now returns a fully-mapped User (bearer flow: POST /auth/login + GET /auth/me)
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch current user
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        const payload = action.payload as { status?: number; message?: string } | undefined;
        const is401 = payload?.status === 401 || payload?.message === 'Unauthorized';

        state.isLoading = false;
        state.isInitialized = true; // Mark as initialized even on failure
        state.user = null;

        // Only clear auth on actual authentication failure (401 Unauthorized)
        // Network errors, timeouts, or server errors should NOT clear auth
        if (is401) {
          state.isAuthenticated = false;
        }
      })
      // Change password
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isInitialized = true; // Keep initialized - we know the auth state (logged out)
      });
  },
});

export const { setUser, clearError } = authSlice.actions;
export default authSlice.reducer;
